import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, Search, AlertCircle, ScrollText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { gameRedisService } from '../services/gameRedis';
import { configService } from '../services/usersApi';
import { PlayerStreamLogData, PlayerStreamLogEntry, ServerConfig } from '../types/player';
import { PlayerProfile } from '../components/PlayerProfile';
import { ClusterSelector } from '../components/ClusterSelector';
import { formatDuration } from '../utils/time';
import { ClusterStatsSummary } from '../components/ClusterStatsSummary';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { RedisKeyInfo } from '../components/RedisKeyInfo';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { EmptyState } from '../components/ui/EmptyState';
import { DataFreshness } from '../components/DataFreshness';
import { useSwrLoader } from '../hooks/useSwrLoader';
import { read } from '../services/swrCache';

const STAT_DISPLAY_NAMES: Record<string, string> = {
  mc_distance: 'Distance Traveled',
  mc_distance_crouched: 'Distance Crouched',
  mc_distance_fallen: 'Distance Fallen',
  mc_distance_sprinted: 'Distance Sprinted',
  playtime: 'Playtime (sec)',
  mc_distance_walked: 'Distance Walked',
  mc_jumps: 'Jumps',
  mc_blocks_broken: 'Blocks Broken',
  mc_distance_flown: 'Distance Flown',
  mc_mobs_killed: 'Mobs Killed',
  mc_distance_swum: 'Distance Swum',
  mc_deaths: 'Deaths',
  mc_distance_climbed: 'Distance Climbed',
  mc_players_killed: 'Players Killed',
};

const LogEntryCard = ({ entry }: { entry: PlayerStreamLogEntry; query: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { id, msg } = entry;
  const { uuid, username, ip, playtime, ...stats } = msg;

  const endTime = new Date(parseInt(id.split('-')[0]));
  const playtimeSeconds = parseInt(playtime);
  const startTime = new Date(endTime.getTime() - playtimeSeconds * 1000);

  return (
    <motion.div
      className="relative pl-6 md:pl-8 py-1.5"
    >
      <div className="absolute left-0 top-0 h-full w-px bg-glass-200/20"></div>
      <div className="absolute left-[-5px] top-[1.375rem] w-[11px] h-[11px] bg-accent-400 rounded-full border-2 border-dark-800"></div>
      
      <div className="glass-card overflow-hidden">
        <div 
          className="flex flex-wrap items-center justify-between px-3 py-2.5 cursor-pointer gap-2 hover:bg-glass-200/5 transition-colors"
          onClick={() => setIsOpen(!isOpen)}
          role="button"
          aria-expanded={isOpen}
        >
          <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0">
            <PlayerProfile uuid={uuid} name={username} />
            <span className="badge badge-success tabular-nums" title="Session length">
              {formatDuration(playtimeSeconds)}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-glass-400 tabular-nums">{startTime.toLocaleTimeString()} – {endTime.toLocaleTimeString()}</span>
            <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
              <ChevronDown className="w-4 h-4 text-glass-400" />
            </motion.div>
          </div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t border-glass-200/10"
            >
              <div className="p-3 md:p-4">
                <h4 className="eyebrow mb-2">Session Stats</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1 text-sm">
                  {Object.entries(stats).map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-2 py-1 border-b border-glass-200/10 min-w-0">
                      <span className="text-xs text-glass-400 truncate">{STAT_DISPLAY_NAMES[key] || key}</span>
                      <span className="text-white font-medium tabular-nums shrink-0">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export function PlayerStreamLogView() {
  useDocumentTitle('Player Stream Log');
  const { clusterId } = useParams();
  const navigate = useNavigate();

  const [configs, setConfigs] = useState<ServerConfig[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<string>('');
  const [logData, setLogData] = useState<PlayerStreamLogData | null>(null);
  const [logDataKey, setLogDataKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleLogCount, setVisibleLogCount] = useState(100);
  const { status: freshness, load } = useSwrLoader();

  useEffect(() => {
    async function fetchClusters() {
      try {
        const configList = await configService.getConfigs();
        const uniqueClusters = Array.from(new Set(configList.map(c => c.cluster_name)))
          .map(name => configList.find(c => c.cluster_name === name)!)
          .filter(Boolean);

        setConfigs(uniqueClusters);
        
        if (clusterId && uniqueClusters.some(c => c.cluster_name === clusterId)) {
          setSelectedCluster(clusterId);
        } else if (uniqueClusters.length > 0) {
          const defaultCluster = uniqueClusters[0].cluster_name;
          setSelectedCluster(defaultCluster);
          navigate(`/player-stream-log/${defaultCluster}`, { replace: true });
        } else { 
          setLoading(false); 
        }
      } catch (err) {
        setError('Failed to fetch cluster list.');
        setLoading(false);
      }
    }
    fetchClusters();
  }, []);

  // Cache, then recache on load: the last copy of this cluster's stream paints at once (if there is
  // one), Redis is re-read every time, and the fresh stream replaces it when it arrives.
  const fetchDataForCluster = useCallback(async (force = false) => {
    if (!selectedCluster) return;
    setLoading(true);
    setError(null);
    let superseded = false;
    try {
      const outcome = await load(
        [
          read(`redis:cluster:${selectedCluster}:player-stream`, () => gameRedisService.getPlayerStreamLog(selectedCluster), (result) => {
            setLogData(result.data);
            setLogDataKey(result.key);
          }),
        ],
        { force, onCached: () => setLoading(false) }
      );
      superseded = outcome.superseded;
      if (superseded) return;
      // 'stale' keeps the cached entries on screen; the freshness badge reports the failed re-read.
      if (outcome.status.phase === 'error') setError(`Failed to fetch stream log for cluster ${selectedCluster}.`);
    } catch (err) {
      setError(`Failed to fetch stream log for cluster ${selectedCluster}.`);
    } finally {
      if (!superseded) setLoading(false);
    }
  }, [selectedCluster, load]);

  useEffect(() => {
    if (selectedCluster && selectedCluster !== clusterId) {
      navigate(`/player-stream-log/${selectedCluster}`);
    }
    // Reset visible count when cluster changes
    setVisibleLogCount(100);
    fetchDataForCluster();
  }, [selectedCluster, clusterId, navigate, fetchDataForCluster]);

  const sortedAndFilteredLogs = useMemo(() => {
    let logs = logData?.XRANGE.slice().reverse() || [];

    if (searchQuery) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      logs = logs.filter(entry => {
        const { username, uuid, ip } = entry.msg;
        return (
          username.toLowerCase().includes(lowerCaseQuery) ||
          uuid.toLowerCase().includes(lowerCaseQuery) ||
          ip.toLowerCase().includes(lowerCaseQuery)
        );
      });
    }

    return logs;
  }, [logData, searchQuery]);

  const visibleLogs = useMemo(() => {
    return sortedAndFilteredLogs.slice(0, visibleLogCount);
  }, [sortedAndFilteredLogs, visibleLogCount]);

  return (
    <div className="space-y-6">
      <ClusterSelector
        eyebrow="Players"
        title="Player Stream Log"
        description="View recent player session activity by cluster."
        configs={configs}
        selectedCluster={selectedCluster}
        onSelect={setSelectedCluster}
      />
      
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
          <input
            type="text"
            placeholder="Filter logs by username, UUID, or IP..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="glass-input w-full pl-9"
          />
        </div>
        <div className="flex items-center gap-2 md:justify-end">
          {!loading && !error && (
            <span className="text-xs text-glass-400 tabular-nums">
              {searchQuery
                ? `${sortedAndFilteredLogs.length.toLocaleString()} match${sortedAndFilteredLogs.length === 1 ? '' : 'es'}`
                : `${sortedAndFilteredLogs.length.toLocaleString()} sessions`}
            </span>
          )}
          <DataFreshness status={freshness} onRefresh={() => fetchDataForCluster(true)} />
          <RedisKeyInfo redisKey={logDataKey} />
        </div>
      </div>

      {loading && (
        <div className="space-y-4" aria-busy="true">
          <div className="glass-card p-4 md:p-5">
            <SkeletonLoader className="h-4 w-48 mb-4" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <SkeletonLoader className="h-3 w-20" />
                  <SkeletonLoader className="h-6 w-16" />
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-3 pl-6 md:pl-8">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="glass-card px-3 py-2.5 flex items-center gap-3">
                <SkeletonLoader className="w-8 h-8 rounded-md" />
                <SkeletonLoader className="h-4 w-32" />
                <SkeletonLoader className="h-5 w-16 rounded-full" />
                <SkeletonLoader className="h-3 w-36 ml-auto" />
              </div>
            ))}
          </div>
        </div>
      )}
      {error && (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load stream log"
          description={error}
        />
      )}
      
      {!loading && !error && sortedAndFilteredLogs.length > 0 && (
        <ClusterStatsSummary logs={sortedAndFilteredLogs} />
      )}

      {!loading && !error && (
        visibleLogs.length > 0 ? (
          <div className="flex flex-col">
            {visibleLogs.map(entry => (
              <LogEntryCard key={entry.id} entry={entry} query={searchQuery} />
            ))}
            {visibleLogCount < sortedAndFilteredLogs.length && (
              <div className="flex justify-center py-4">
                <button
                  onClick={() => setVisibleLogCount(prev => prev + 100)}
                  className="glass-button"
                >
                  Show More ({sortedAndFilteredLogs.length - visibleLogCount} remaining)
                </button>
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            icon={searchQuery ? Search : ScrollText}
            title={searchQuery ? 'No matching sessions' : 'No stream log data'}
            description={searchQuery 
                ? 'No logs match your search query.' 
                : 'No stream log data found for this cluster.'}
            action={searchQuery ? <button onClick={() => setSearchQuery('')} className="glass-button-secondary">Clear search</button> : undefined}
          />
        )
      )}
    </div>
  );
}
